import React, { useState, useEffect, useRef, useCallback } from 'react';
// Assuming Clerk is used for user context
import { useUser } from '@clerk/clerk-react';
// Assuming React Router for navigation
import { useNavigate } from 'react-router-dom'; // Added for sidebar navigation
import axios from 'axios'; // Make sure axios is installed
// Simple Markdown rendering (install react-markdown: npm i react-markdown)
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'; // For code blocks
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'; // Code block style
import remarkGfm from 'remark-gfm'; // For GitHub Flavored Markdown (tables, etc.)

// Import Lucide Icons - Combined List (Updated for simplified UI)
import {
  // Sidebar Needs (Simplified):
  LayoutDashboard, // Back to Dashboard
  Mail, // User Email Icon
  // Top Bar Needs:
  Search, Bell, ChevronDown, // Top Bar / General
  Menu, // Services Menu Icon
  Globe, // Region Icon
  LifeBuoy, // Support/Help Icon
  // Page Functionality Needs:
  Cpu, // Models Icon (Sidebar placeholder & Model Column)
  Loader2, // Loading State
  X, // Close Modal / Cancel (If needed later)
  ShieldAlert, // Warning / Error Icon
  Send, // Send Prompt Icon
  ClipboardCopy, // Copy Response
  Check, // Success Icon (e.g., for Copy)
  Scale, // Page Title Icon / Comparison Icon
  BrainCircuit, // Reasoning Icon
  // Retained but likely unused:
  Settings, Users, Bot, Database, Link, BookOpen, Wrench, FlaskConical, HelpCircle, Server, Activity, DollarSign
} from 'lucide-react';

// --- API Configuration (PRESERVED) ---
const API_BASE_URL = 'https://web-production-5ee8.up.railway.app'; // Your backend API base URL

// --- Styled Clickable Components (Unchanged) ---
const ClickableLink = ({ children, className, href = '#', target, rel, onClick, disabled, title, ...props }) => (
  <a
    href={!disabled ? href : undefined}
    target={target}
    rel={target === '_blank' ? 'noopener noreferrer' : rel}
    onClick={!disabled ? onClick : (e) => e.preventDefault()}
    role="button"
    tabIndex={disabled ? -1 : 0}
    className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`}
    aria-disabled={disabled}
    title={title || (typeof children === 'string' ? children : '')}
    {...props}
  >
    {children}
  </a>
);

const ClickableDiv = ({ children, className, onClick, disabled, title, ...props }) => (
  <div
    role="button"
    tabIndex={disabled ? -1 : 0}
    onClick={!disabled ? onClick : undefined}
    onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }}
    className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`}
    aria-disabled={disabled}
    title={title || (typeof children === 'string' ? children : '')}
    {...props}
  >
    {children}
  </div>
);

// --- Sidebar Nav Item Component (Reused Structure) ---
const SidebarNavItem = ({ icon: Icon, text, href, active, onClick }) => (
     <a href={href} onClick={onClick} className={`flex items-center pl-3 pr-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 group relative text-gray-400 hover:text-white hover:bg-gray-800/50`}>
         {/* Active state indicator removed */}
         <Icon className={`w-4 h-4 mr-2.5 flex-shrink-0 text-gray-500 group-hover:text-gray-300`} strokeWidth={1.5} />
         <span className="truncate">{text}</span>
     </a>
);

// --- Simplified Sidebar Component ---
const Sidebar = () => {
    const navigate = useNavigate(); // Use navigate for routing
    const { user, isLoaded } = useUser(); // Get user object and loading state

    const handleBackToDashboardClick = (e) => {
        e.preventDefault(); // Prevent default anchor tag behavior
        console.log("Navigating back to /echidna/efoundry/dashboard");
        navigate('/echidna/efoundry/dashboard'); // Navigate using React Router
    };

    // Use primary email address, provide loading/fallback state
    const userEmail = isLoaded && user ? user.primaryEmailAddress?.emailAddress : 'Loading...';

    return (
        <div className="w-52 md:w-56 h-screen bg-black border-r border-gray-800/50 flex flex-col flex-shrink-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black">
            {/* Header */}
            <div className="h-14 flex items-center px-3 flex-shrink-0 border-b border-gray-800/50">
                <div className="flex items-center">
                    <svg className="w-6 h-6 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#8B5CF6" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 17L12 22L22 17" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 12L12 17L22 12" stroke="#8B5CF6" strokeWidth="1.5"/></svg>
                    <span className="text-base font-bold text-white">Echidna Foundry</span>
                </div>
            </div>

            {/* Simplified Navigation Area */}
            <div className="flex-grow py-2">
                <nav className="space-y-0.5 px-2 mt-3"> {/* Added mt-3 for spacing */}
                    <SidebarNavItem
                        key="back-dashboard"
                        icon={LayoutDashboard}
                        text="Back to Dashboard"
                        href="/echidna/efoundry/dashboard" // href for context/right-click
                        active={false} // This item is never 'active' within this view
                        onClick={handleBackToDashboardClick} // Use navigate handler
                    />
                </nav>
            </div>

            {/* Updated User Info Section */}
            <div className="p-2 border-t border-gray-800/50 mt-auto">
                 <ClickableLink
                    href="#profile" // Placeholder href
                    title={userEmail} // Tooltip shows the email
                    className="flex items-center p-2 rounded-md text-xs text-gray-400 hover:bg-gray-800/50 hover:text-white group w-full" // Added w-full
                 >
                    <Mail className="w-4 h-4 mr-2 text-gray-500 group-hover:text-gray-300 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate font-medium">{userEmail}</span>
                 </ClickableLink>
             </div>
        </div>
    );
};


// --- Top Bar Component (Updated User Info Fallback) ---
const TopBar = ({ pageTitle }) => {
    const { user } = useUser();
    // Use email as primary fallback for display name and initials
    const userInitial = user?.firstName?.charAt(0) || user?.lastName?.charAt(0) || user?.primaryEmailAddress?.emailAddress?.[0] || '?';
    const userDisplayName = user?.firstName ? `${user.firstName}${user.lastName ? ' ' + user.lastName.charAt(0) + '.' : ''}` : user?.primaryEmailAddress?.emailAddress || 'Account';

  return (
      <div className="h-14 bg-gray-950 border-b border-gray-800/50 flex items-center justify-between px-4 flex-shrink-0 sticky top-0 z-20">
          <div className="flex items-center space-x-3">
              <ClickableDiv className="p-1.5 -m-1.5 rounded hover:bg-gray-800/50 text-gray-300 hover:text-white" title="Services Menu">
                  <Menu className="w-5 h-5" strokeWidth={1.5}/>
              </ClickableDiv>
              <span className="text-sm font-medium text-white">{pageTitle}</span>
          </div>
          <div className="flex items-center space-x-3 md:space-x-4">
              <div className="relative hidden md:block">
                  <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={1.5}/>
                  <input type="search" placeholder={`Search ${pageTitle}...`} className="bg-gray-800/60 border border-gray-700/50 rounded-md pl-8 pr-2 py-1 w-48 lg:w-64 text-xs text-gray-200 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none focus:bg-gray-800" />
              </div>
              <ClickableDiv className="flex items-center text-xs text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50" title="Region">
                  <Globe className="w-4 h-4 mr-1" strokeWidth={1.5}/>
                  <span>us-east-1</span>
              </ClickableDiv>
              <ClickableLink href="#help" title="Support" className="text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                  <LifeBuoy className="w-5 h-5" strokeWidth={1.5}/>
              </ClickableLink>
              <ClickableDiv title="Notifications" className="text-gray-400 hover:text-white relative p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                  <Bell className="w-5 h-5" strokeWidth={1.5}/>
              </ClickableDiv>
              <ClickableDiv title="Account" className="flex items-center text-xs text-gray-300 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                  <span className="hidden md:inline mr-1 truncate max-w-[100px]" title={userDisplayName}>{userDisplayName}</span>
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">{userInitial.toUpperCase()}</span>
                  <ChevronDown className="w-4 h-4 ml-1 text-gray-500" strokeWidth={1.5}/>
              </ClickableDiv>
          </div>
      </div>
  );
};


// --- Helper Function to Parse AI Response with Reasoning (REVERTED & REFINED PARSER) ---
function parseReasoningResponse(rawText) {
    if (typeof rawText !== 'string') {
        console.warn("[Reverted Parser] parseReasoningResponse received non-string input:", rawText);
        return { reasoning: null, response: String(rawText) }; // Attempt to stringify non-string input
    }
    const reasonStartTag = '<reason>';
    const reasonEndTag = '</reason>';
    const separator = '\n\n'; // Expected separator

    let reasoning = null;
    let response = rawText; // Default response is the full text

    const startIdx = rawText.indexOf(reasonStartTag); // Find the *first* occurrence

    if (startIdx !== -1) {
        // Found the start tag *somewhere*
        const endIdx = rawText.indexOf(reasonEndTag, startIdx + reasonStartTag.length);

        if (endIdx !== -1) {
            // Found both tags in sequence
            reasoning = rawText.substring(startIdx + reasonStartTag.length, endIdx).trim();

            // --- Determine the main 'response' part ---
            // Strategy: Assume response is either text strictly BEFORE <reason> OR strictly AFTER </reason> (potentially with separator)
            const textBefore = rawText.substring(0, startIdx).trim();
            const textAfter = rawText.substring(endIdx + reasonEndTag.length); // Text immediately after </reason>

            // Prioritize text AFTER if it exists and is separated or the only other text
            if (textAfter.trimStart().startsWith(separator)) {
                // Separator found after tag
                const separatorIndexInFollowing = textAfter.indexOf(separator);
                response = textAfter.substring(separatorIndexInFollowing + separator.length).trimStart();
                if (textBefore.length > 0) {
                    console.warn("[Reverted Parser] Found text before <reason> tag, which is being ignored due to separator after </reason>.");
                }
            } else if (textAfter.trim().length > 0 && textBefore.length === 0) {
                 // Only text AFTER tag (no separator)
                 response = textAfter.trimStart();
                 console.log("[Reverted Parser] No separator after </reason>, using remaining text as response (no text before).");
             } else if (textAfter.trim().length === 0 && textBefore.length > 0) {
                 // Only text BEFORE tag
                 response = textBefore;
                 console.log("[Reverted Parser] Using text before <reason> as response because nothing significant follows </reason>.");
             } else if (textAfter.trim().length > 0 && textBefore.length > 0) {
                 // AMBIGUOUS: Text before AND after, no separator. Defaulting to AFTER, log error.
                 response = textAfter.trimStart();
                 console.error("[Reverted Parser] AMBIGUOUS RESPONSE: Found text before <reason> AND after </reason> without separator. Defaulting to text AFTER.");
             } else {
                 // Only reasoning content between tags found.
                 response = null;
                 console.log("[Reverted Parser] Only reasoning content found between tags, no other text.");
             }

        } else {
            // Incomplete tag found somewhere: Don't parse as reasoning, treat whole as response.
            console.warn("[Reverted Parser] Found <reason> but no matching </reason> tag. Treating entire text as response.");
            reasoning = null; // Avoid incorrectly assigning partial text as reasoning
            response = rawText.trim();
        }
    } else {
        // No <reason> tag found at all. The entire text is the response.
        reasoning = null;
        response = rawText.trim();
    }

    // Ensure empty strings become null
    if (reasoning === '') reasoning = null;
    if (response === '') response = null;

    // Final log of what was parsed
    console.log("[Reverted Parser] Final Parsed Reasoning:", reasoning ? `"${reasoning.substring(0,50)}..."` : null);
    console.log("[Reverted Parser] Final Parsed Response:", response ? `"${response.substring(0,50)}..."` : null);
    return { reasoning, response };
}



// --- Markdown Components Configuration (Unchanged) ---
const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || '');
        return !inline && match ? (
            <SyntaxHighlighter
                style={vscDarkPlus}
                PreTag="div"
                language={match[1]}
                {...props}
                className="scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800" // Add scrollbar styling
            >
                {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
        ) : (
            <code className="bg-gray-700/50 px-1 py-0.5 rounded text-xs font-mono" {...props}>
                {children}
            </code>
        );
    },
    p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
    ul: ({ node, ...props }) => <ul className="list-disc list-outside mb-2 pl-4" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal list-outside mb-2 pl-4" {...props} />,
    li: ({ node, ...props }) => <li className="mb-1" {...props} />,
    a: ({ node, ...props }) => <a className="text-purple-400 hover:text-purple-300 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
    pre: ({ node, ...props }) => <pre className="bg-gray-900 p-0 rounded-md overflow-hidden" {...props} />,
};


// --- Response Column Component (Unchanged Structure) ---
const ModelResponseColumn = ({ modelId, modelDisplayName, responseData }) => {
  const { response, reasoning, status, error } = responseData || { status: 'idle', response: null, reasoning: null, error: null };
  const [copied, setCopied] = useState(false);
  const hasReasoning = !!reasoning;

  const handleCopy = () => {
      let textToCopy = '';
      if (reasoning && response) {
          textToCopy = `Reasoning:\n${reasoning}\n\nResponse:\n${response}`;
      } else {
          textToCopy = response || reasoning || '';
      }
      if (!textToCopy) return;
      navigator.clipboard.writeText(textToCopy).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
      }).catch(err => console.error('Failed to copy text: ', err));
  };

  const truncatedReasoning = hasReasoning ? (reasoning.length > 70 ? reasoning.substring(0, 70) + '...' : reasoning) : '';

  return (
      <div className="bg-gray-950 rounded-lg border border-gray-800/50 flex flex-col h-full shadow-sm min-h-0">
          {/* Header */}
          <div className="flex justify-between items-center p-3 border-b border-gray-800/50 flex-shrink-0 min-h-[53px]">
              <div className="flex items-start gap-2 min-w-0">
                  <Cpu className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-gray-100 truncate" title={modelDisplayName}>{modelDisplayName}</span>
                       {hasReasoning && status === 'success' && (
                           <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5" title={`Reasoning provided: ${reasoning}`}>
                               <BrainCircuit className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
                               <span className="truncate italic">{truncatedReasoning}</span>
                           </div>
                       )}
                  </div>
              </div>
               {(status === 'success' && (response || reasoning)) && (
                  <ClickableDiv onClick={handleCopy} title={copied ? "Copied!" : "Copy Response/Reasoning"} className="p-1 -m-1 text-gray-500 hover:text-purple-400 hover:bg-gray-800/50 rounded self-start">
                      {copied ? <Check className="w-3.5 h-3.5 text-green-500" strokeWidth={2} /> : <ClipboardCopy className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  </ClickableDiv>
               )}
          </div>
          {/* Body */}
          <div className="p-4 flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-gray-950 min-h-[150px]">
              {status === 'loading' && (
                  <div className="flex items-center justify-center h-full text-gray-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
              )}
              {status === 'error' && (
                   <div className="text-center text-red-400 text-xs p-4 bg-red-900/20 border border-red-700/30 rounded">
                      <ShieldAlert className="w-5 h-5 mx-auto mb-1" />Error: {error || 'Failed to get response'}
                   </div>
              )}
               {status === 'success' && (
                  <>
                      {response ? (
                         <div className="prose prose-sm prose-invert max-w-none text-gray-300 text-xs leading-relaxed">
                              <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                                 {response}
                              </ReactMarkdown>
                          </div>
                      ) : ( hasReasoning && <div className="text-center text-gray-600 text-xs italic py-4">*No main response content provided.*</div> )}

                      {hasReasoning && (
                          <details className="mt-4 border-t border-gray-800 pt-3" open={!response}> {/* Open reasoning if no main response exists */}
                              <summary className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer font-medium outline-none select-none flex items-center gap-1.5">
                                  <BrainCircuit className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                  Reasoning Steps
                              </summary>
                              <div className="mt-2 text-xs text-gray-300 bg-gray-900 p-3 rounded-md border border-gray-700/50 prose prose-xs prose-invert max-w-none">
                                  <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                                      {reasoning}
                                  </ReactMarkdown>
                              </div>
                          </details>
                      )}
                      {!response && !hasReasoning && status === 'success' && (
                          <div className="text-center text-gray-600 text-xs italic pt-10">*No response or reasoning received.*</div>
                      )}
                  </>
              )}
              {status === 'idle' && (
                  <div className="text-center text-gray-600 text-xs italic pt-10">Response will appear here...</div>
              )}
          </div>
      </div>
  );
};


// --- Model Comparison Content Component (API URL Verified, Using REVERTED Parser) ---
const ModelComparisonContent = () => {
  const { user } = useUser();
  const cid = user?.id || 'guest-user-id';

  // State
  const [prompt, setPrompt] = useState('');
  const modelsToCompare = [
      { id: 'echidnav4o', displayName: 'Echidna v4o', endpoint: '/echidna/echidnav4o' },
      { id: 'echidnav5turbo', displayName: 'Echidna 5-Turbo', endpoint: '/echidna/echidnav5turbo' }
  ];
  const [responses, setResponses] = useState(() => {
      const initial = {};
      modelsToCompare.forEach(model => {
          initial[model.id] = { response: null, reasoning: null, status: 'idle', error: null };
      });
      return initial;
  });
  const [isSendingPrompt, setIsSendingPrompt] = useState(false);
  const [error, setError] = useState(null);
  const textAreaRef = useRef(null);

  // Send Prompt to Models (Using original API_BASE_URL)
  const handleSendPrompt = async () => {
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt) { setError("Please enter a prompt."); return; }
      setIsSendingPrompt(true);
      setError(null);

      const initialResponses = {};
      modelsToCompare.forEach(model => {
          initialResponses[model.id] = { response: null, reasoning: null, status: 'loading', error: null };
      });
      setResponses(initialResponses);

      const payload = { messages: [{ role: 'user', content: trimmedPrompt }] };

      const responsePromises = modelsToCompare.map(model => {
          // *** VERIFIED API Request URL ***
          const requestUrl = `${API_BASE_URL}${model.endpoint}`;
          console.log(`Sending prompt to model ${model.id} at ${requestUrl} with payload:`, JSON.stringify(payload));

          return axios.post(requestUrl, payload, {
              headers: { 'Content-Type': 'application/json' },
          })
              .then(axiosResponse => {
                   console.log(`Raw axiosResponse.data from ${model.id}:`, axiosResponse.data);
                   let rawApiResponse = axiosResponse.data;

                   // Extract nested response if applicable
                   if (typeof rawApiResponse === 'object' && rawApiResponse !== null && 'response' in rawApiResponse) {
                       console.log(`Extracted nested response from ${model.id}`);
                       rawApiResponse = rawApiResponse.response;
                   } else {
                        console.log(`Response from ${model.id} assumed to be direct in data.`);
                   }
                   if (typeof rawApiResponse !== 'string') {
                        console.warn(`Final API response value from ${model.id} was not a string after potential extraction:`, rawApiResponse);
                        rawApiResponse = String(rawApiResponse);
                   }

                   // --- Apply REVERTED (Less Strict) Reasoning Parsing ---
                   console.log(`--- String being passed to PARSER for ${model.id} ---`);
                   console.log(rawApiResponse);
                   console.log(`-----------------------------------------------------`);
                   const { reasoning, response } = parseReasoningResponse(rawApiResponse);
                   // Update state based on parsed values
                   setResponses(prev => ({ ...prev, [model.id]: { response, reasoning, status: 'success', error: null } }));
                   console.log(`Successfully processed response for ${model.id} using REVERTED parser.`);

              })
              .catch(err => {
                  console.error(`Error fetching response from model ${model.id}:`, err);
                  let errorMessage = 'An unknown error occurred.';
                  if (err.response) {
                      console.error(`Error Response Data (${model.id}):`, err.response.data);
                      console.error(`Error Response Status (${model.id}):`, err.response.status);
                       let serverErrorMsg = err.response.data?.error || err.response.data?.message || (typeof err.response.data === 'string' ? err.response.data : '');
                       errorMessage = `Server Error ${err.response.status}: ${serverErrorMsg || 'No specific error message provided.'}`;
                  } else if (err.request) {
                      console.error(`Error Request (${model.id}):`, err.request);
                      errorMessage = 'No response from server. Check API connection, CORS, and server status.';
                  } else {
                      console.error('Axios setup error:', err.message);
                      errorMessage = `Request setup error: ${err.message}`;
                  }
                  setResponses(prev => ({ ...prev, [model.id]: { response: null, reasoning: null, status: 'error', error: errorMessage } }));
              });
      });

      try {
          await Promise.allSettled(responsePromises);
      } finally {
          setIsSendingPrompt(false);
      }
  };

  // Auto-resize textarea (Unchanged)
  useEffect(() => {
      const textarea = textAreaRef.current;
      if (textarea) {
          textarea.style.transition = 'none';
          textarea.style.height = 'auto';
          const scrollHeight = textarea.scrollHeight;
          const maxHeight = 200;
          textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
          textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
          requestAnimationFrame(() => { textarea.style.transition = ''; });
      }
  }, [prompt]);


  return (
      <div className="flex flex-col h-full">
          {/* Header */}
          <header className="mb-6 flex-shrink-0">
              <div className="flex items-center gap-3 mb-1">
                  <Scale className="w-7 h-7 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
                  <h1 className="text-3xl font-bold tracking-tight text-gray-100">Model Comparison</h1>
              </div>
              <p className="text-base text-gray-400 sm:ml-10">Compare responses from base Echidna models side-by-side.</p>
          </header>

          {/* General Error Display */}
          {error && (
              <div className="mb-4 p-3 border border-red-700/50 bg-red-900/20 rounded-lg text-sm text-red-300 flex items-start gap-2 flex-shrink-0">
                  <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" strokeWidth={2}/>
                  <span>{error}</span>
              </div>
          )}

          {/* Response Area */}
           <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-6 overflow-hidden min-h-0">
              {modelsToCompare.map(model => (
                  <ModelResponseColumn
                      key={model.id}
                      modelId={model.id}
                      modelDisplayName={model.displayName}
                      responseData={responses[model.id]}
                  />
              ))}
          </div>

          {/* Input Area */}
          <div className="mt-6 pt-4 border-t border-gray-800/50 flex-shrink-0">
              <div className="relative">
                  <textarea
                      ref={textAreaRef}
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Enter prompt to compare models... (Shift+Enter for new line)"
                      className="w-full bg-gray-900 border border-gray-700 p-3 pr-28 text-sm text-white rounded-md focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600 placeholder-gray-500 resize-none scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 block"
                      rows={1}
                      style={{ minHeight: '44px', boxSizing: 'border-box' }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!isSendingPrompt && prompt.trim()) { handleSendPrompt(); } } }}
                      disabled={isSendingPrompt}
                  />
                  <ClickableDiv
                      onClick={handleSendPrompt}
                      disabled={isSendingPrompt || !prompt.trim()}
                      className={`absolute bottom-2.5 right-2.5 px-4 py-1.5 text-sm border flex items-center gap-2 rounded-md transition-all duration-150 z-10 ${
                          isSendingPrompt || !prompt.trim()
                              ? 'border-gray-700 bg-gray-800 text-gray-500 cursor-not-allowed'
                              : 'border-purple-700/50 bg-purple-600 hover:bg-purple-700 text-white cursor-pointer focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900'
                          }`}
                      title={!prompt.trim() ? "Enter a prompt" : "Compare Models (Enter)"}
                  >
                      {isSendingPrompt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2}/>}
                      <span>{isSendingPrompt ? 'Sending...' : 'Compare'}</span>
                  </ClickableDiv>
              </div>
          </div>
      </div>
  );
};


// --- Main Application Component (Echmodels - Simplified) ---
function Echmodels() {
  // Fixed page title
  const pageTitle = 'Model Comparison';

  // Render content is always ModelComparisonContent
  const renderContent = () => <ModelComparisonContent />;

  return (
      <div className="flex h-screen bg-black font-sans antialiased text-gray-200">
          {/* Use simplified Sidebar */}
          <Sidebar />
          <div className="flex-1 flex flex-col overflow-hidden">
              {/* TopBar displays fixed title and updated user info */}
              <TopBar pageTitle={pageTitle} />
              <main className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black relative bg-black p-6 lg:p-8">
                  <div className="relative z-10 h-full">
                      {renderContent()}
                  </div>
              </main>
          </div>
          {/* Global styles */}
          <style jsx global>{`
              /* Echtools global styles */
              .scrollbar-thin { scrollbar-width: thin; scrollbar-color: theme(colors.gray.700 / 0.5) transparent; }
              .scrollbar-thin::-webkit-scrollbar { width: 5px; height: 5px; }
              .scrollbar-thin::-webkit-scrollbar-thumb { background-color: theme(colors.gray.700 / 0.5); border-radius: 3px; }
              .scrollbar-thin::-webkit-scrollbar-track { background-color: transparent; }
              body { background-color: black; overflow: hidden; margin: 0; } /* Added margin:0 */
              @keyframes bounce-quick { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
              .animate-bounce-quick { animation: bounce-quick 0.3s ease-in-out; }
              @keyframes fade-in-fast { from { opacity: 0; } to { opacity: 1; } }
              .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
              @keyframes spin { to { transform: rotate(360deg); } }
              .animate-spin { animation: spin 1s linear infinite; }

              /* Prose adjustments */
               .prose { font-size: inherit; line-height: inherit; }
               .prose-invert { color: inherit; }
               .prose code::before, .prose code::after { content: ''; }
               .prose pre { background-color: #1e1e1e !important; padding: 0.8em 1em !important; margin-top: 0.5em !important; margin-bottom: 0.8em !important; border-radius: 0.375rem; overflow-x: auto; white-space: pre; position: relative; z-index: 0; }
               .prose pre code { background-color: transparent !important; padding: 0 !important; font-size: 0.875em !important; }
               .prose-sm { font-size: 0.875rem; line-height: 1.6; }
               .prose-xs { font-size: 0.75rem; line-height: 1.5; }
               .prose div[class*="syntaxhighlighter"] { margin: 0 !important; border-radius: inherit; padding: 0 !important; background: transparent !important; }
               .prose code { white-space: pre-wrap; word-wrap: break-word; }
               .prose pre code { white-space: pre; word-wrap: normal; overflow-x: auto;}

               textarea { transition: height 0.1s ease-out; }
          `}</style>
      </div>
  );
}

// Exporting as Echmodels
export default Echmodels;